import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Play, RotateCcw } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/misc'
import { getPreviewPrompt, rerunPreview } from '@/lib/api/previews/run-preview'
import { apiErrorMessage } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import type { PreviewResult } from '@/types/preview'

interface PromptEditorProps {
  sessionId: string
  domains: string
  onResult: (r: PreviewResult) => void
}

/**
 * Edit the extraction prompt and re-run against this document only.
 *
 * The prompt is stored with the preview session, so experiments never touch the
 * shared prompt set or any other document. Re-running skips parsing and
 * indexing — it is just the LLM step, a couple of seconds.
 */
export function PromptEditor({ sessionId, domains, onResult }: PromptEditorProps) {
  const { t } = useTranslation()
  const [text, setText] = useState('')
  const [running, setRunning] = useState(false)

  const stored = useQuery({
    queryKey: ['preview-prompt', sessionId, domains],
    queryFn: () => getPreviewPrompt(sessionId, domains),
  })

  // Seed the editor once the stored prompt arrives, without clobbering edits.
  useEffect(() => {
    if (stored.data && !text) setText(stored.data.prompt)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored.data])

  const dirty = !!stored.data && text !== stored.data.prompt
  const isDefault = !!stored.data && text === stored.data.default_prompt

  const run = async () => {
    setRunning(true)
    try {
      const res = await rerunPreview(sessionId, text, domains)
      onResult(res)
      toast.success(t('units.terms', { count: res.term_count }), {
        description: t('promptEditor.rerunDone'),
      })
      stored.refetch()
    } catch (e) {
      toast.error(t('promptEditor.rerunFailed'), { description: apiErrorMessage(e) })
    } finally {
      setRunning(false)
    }
  }

  if (stored.isLoading) {
    return (
      <div className="space-y-2 p-4">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        <span className="text-xs font-medium">{t('promptEditor.heading')}</span>
        {stored.data?.is_custom && <Badge variant="warning">{t('promptEditor.custom')}</Badge>}
        {dirty && <Badge variant="primary">{t('promptEditor.unsaved')}</Badge>}
        <span className="text-[11px] text-muted-foreground">{t('promptEditor.scope')}</span>
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={running || isDefault}
            onClick={() => setText(stored.data?.default_prompt ?? '')}
            title={t('promptEditor.resetHint')}
          >
            <RotateCcw />
            {t('promptEditor.reset')}
          </Button>
          <Button size="sm" disabled={running || !text.trim()} onClick={run}>
            {running ? <Loader2 className="animate-spin" /> : <Play />}
            {running ? t('promptEditor.running') : t('promptEditor.run')}
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 p-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          spellCheck={false}
          disabled={running}
          className={cn(
            'scrollbar-thin h-full w-full resize-none rounded-md border border-border bg-card p-3',
            'font-mono text-[12px] leading-relaxed',
            'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            running && 'opacity-60',
          )}
        />
      </div>

      <p className="shrink-0 border-t border-border px-4 py-1.5 text-[11px] text-muted-foreground">
        {t('promptEditor.footer')}
      </p>
    </div>
  )
}
